/**
 * PODeliveryStep — Step 3 of POTransactionsStepper
 * Collect delivery date and location (dialog-based picker).
 * Optional delivery notes.
 */
import { useState, useMemo } from 'react';
import {
  Box, Typography, Grid, TextField, Paper, Dialog, DialogTitle, DialogContent,
  Button, InputAdornment, Table, TableHead, TableBody, TableRow, TableCell,
  IconButton, alpha, useTheme
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Search as SearchIcon, Close as CloseIcon, LocationOn as LocationIcon } from '@mui/icons-material';
import dayjs from 'dayjs';

function LocationPickerButton({ location, onClick }) {
  const theme = useTheme();
  return (
    <Box>
      <Button
        fullWidth variant="outlined" onClick={onClick}
        sx={{
          justifyContent: location ? 'flex-start' : 'space-between',
          textAlign: 'left', minHeight: 40, borderRadius: 2,
          backgroundColor: 'rgba(255,255,255,0.9)',
          borderColor: location ? alpha(theme.palette.primary.main, 0.5) : alpha(theme.palette.primary.main, 0.25),
          color: location ? 'text.primary' : 'text.secondary',
          fontWeight: 500, px: 2, py: 0, minHeight: 40, textTransform: 'none', fontSize: '0.9rem', display: 'block',
          '&:hover': { borderColor: theme.palette.primary.main, boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}` }
        }}
      >
        {location ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LocationIcon sx={{ fontSize: 18, color: 'primary.main' }} />
            {location.name}
          </Box>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span>Delivery Location *</span>
            <SearchIcon sx={{ fontSize: 20, opacity: 0.5 }} />
          </Box>
        )}
      </Button>
    </Box>
  );
}

export default function PODeliveryStep({ deliveryInfo, onChange, locations }) {
  const theme = useTheme();
  const [locDialogOpen, setLocDialogOpen] = useState(false);
  const [locSearch, setLocSearch] = useState('');

  const set = (field, value) => onChange({ ...deliveryInfo, [field]: value });

  const selectedLocation = (locations || []).find(l => l.id === deliveryInfo.locationId) || null;

  const sortedLocations = useMemo(() => {
    const locs = [...(locations || [])];
    return locs.sort((a, b) => {
      if (a.is_main && !b.is_main) return -1;
      if (!a.is_main && b.is_main) return 1;
      return (a.name || '').localeCompare(b.name || '');
    });
  }, [locations]);

  const filteredLocations = useMemo(() => {
    if (!locSearch.trim()) return sortedLocations;
    const q = locSearch.toLowerCase();
    return sortedLocations.filter(l => l.name?.toLowerCase().includes(q));
  }, [sortedLocations, locSearch]);

  const tfSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 2, backgroundColor: 'rgba(255,255,255,0.9)',
      '& .MuiOutlinedInput-notchedOutline': { borderColor: alpha(theme.palette.primary.main, 0.25) },
      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main },
    },
    '& .MuiInputLabel-root': { fontWeight: 500 }
  };

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Specify when and where this delivery is expected. These details will be recorded on all line items.
      </Typography>

      <Paper elevation={0} sx={{
        p: 2.5, borderRadius: 2,
        border: `1px solid ${alpha(theme.palette.grey[400], 0.35)}`,
        bgcolor: alpha(theme.palette.grey[50], 0.8)
      }}>
        <Typography variant="subtitle2" sx={{
          mb: 2, fontWeight: 500, color: 'text.secondary',
          fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.6px'
        }}>
          Delivery Details
        </Typography>

        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Grid container spacing={2} alignItems="flex-end">
            <Grid item xs={12} md={6}>
              <DatePicker
                label="Expected Delivery Date"
                value={deliveryInfo.expectedDate ? dayjs(deliveryInfo.expectedDate) : null}
                onChange={v => set('expectedDate', v ? v.format('YYYY-MM-DD') : '')}
                slotProps={{ textField: { size: 'small', fullWidth: true, sx: tfSx } }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <LocationPickerButton
                location={selectedLocation}
                onClick={() => { setLocSearch(''); setLocDialogOpen(true); }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth size="small" label="Delivery Notes"
                value={deliveryInfo.notes || ''}
                onChange={e => set('notes', e.target.value)}
                placeholder="Optional notes..."
                sx={tfSx}
              />
            </Grid>
          </Grid>
        </LocalizationProvider>
      </Paper>

      {deliveryInfo.locationId && (
        <Box sx={{
          mt: 2, p: 1.5, borderRadius: 2,
          bgcolor: alpha(theme.palette.success.main, 0.06),
          border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`
        }}>
          <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 500 }}>
            Location: {selectedLocation?.name}
            {deliveryInfo.expectedDate ? ` · Delivery: ${deliveryInfo.expectedDate}` : ''}
          </Typography>
        </Box>
      )}

      {/* Location search dialog */}
      <Dialog
        open={locDialogOpen}
        onClose={() => setLocDialogOpen(false)}
        maxWidth="lg" fullWidth
        PaperProps={{ sx: { borderRadius: 3, minHeight: '60vh', width: '80vw', maxWidth: '1200px' } }}
        sx={{ '& .MuiBackdrop-root': { backgroundColor: 'rgba(0,0,0,0.8)' } }}
      >
        <DialogTitle sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          pb: 3, pt: 3, borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`
        }}>
          <Typography variant="h5" sx={{ fontWeight: 500 }}>Select Location</Typography>
          <IconButton onClick={() => setLocDialogOpen(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 4 }}>
          <TextField
            fullWidth placeholder="Search locations..."
            value={locSearch}
            onChange={e => setLocSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
            sx={{ mt: 3, mb: 4, '& .MuiOutlinedInput-root': { borderRadius: 2, fontSize: '1rem' } }}
          />
          <Table sx={{ '& .MuiTableCell-root': { fontSize: '1rem', py: 2.5 } }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 500 }}>Location</TableCell>
                <TableCell align="right" sx={{ fontWeight: 500 }}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredLocations.map(loc => (
                <TableRow key={loc.id} hover sx={{ '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) } }}>
                  <TableCell>{loc.name}</TableCell>
                  <TableCell align="right">
                    <Button
                      variant="outlined"
                      onClick={() => { set('locationId', loc.id); setLocDialogOpen(false); setLocSearch(''); }}
                      sx={{ borderRadius: 2, px: 3, py: 1, fontSize: '0.95rem' }}
                    >
                      Select
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {filteredLocations.length === 0 && (
                <TableRow>
                  <TableCell colSpan={2} align="center">
                    <Typography color="text.secondary" sx={{ py: 4, fontSize: '1rem' }}>No locations found</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
